import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

export default function Header() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);

  const currentLang = lang === 'en' ? 'en' : 'it';

  useEffect(() => {
    if (i18n.language !== currentLang) i18n.changeLanguage(currentLang);
  }, [currentLang, i18n]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const home = `/${currentLang}`;

  const goAnchor = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (location.pathname !== home) {
      navigate(home);
      setTimeout(scroll, 60);
    } else {
      scroll();
    }
  };

  const switchLang = (target) => {
    if (target === currentLang) return;
    // preserve scroll position — do not trigger ScrollToTop's scroll on lang switch.
    const p = location.pathname;
    let newPath;
    if (/^\/(it|en)(\/|$)/.test(p)) {
      newPath = p.replace(/^\/(it|en)/, `/${target}`);
    } else if (p === '/thank_you') {
      // Thank you page is Italian-only — stay put but still change lang for future pages.
      i18n.changeLanguage(target);
      return;
    } else {
      newPath = `/${target}`;
    }
    // remember current scroll and preserve it after route change
    const y = window.scrollY;
    navigate(newPath + location.hash);
    // ScrollToTop already skips lang-only changes; also ensure smoothness
    requestAnimationFrame(() => window.scrollTo({ top: y }));
  };

  return (
    <header className={`auro-header ${scrolled ? 'is-scrolled' : ''}`} data-testid="site-header">
      <div className="auro-header__inner">
        <NavLink to={home} className="auro-header__brand" aria-label="Auronixio">
          <Logo />
        </NavLink>

        <nav className="auro-header__nav" aria-label={t('nav.menu')}>
          <a href="#how" onClick={goAnchor('how')}>{t('nav.how')}</a>
          <a href="#benefits" onClick={goAnchor('benefits')}>{t('nav.benefits')}</a>
          <a href="#transparency" onClick={goAnchor('transparency')}>{t('nav.transparency')}</a>
          <a href="#faq" onClick={goAnchor('faq')}>{t('nav.faq')}</a>
          <a href="#contact" onClick={goAnchor('contact')} className="auro-header__cta">{t('nav.contact')}</a>
        </nav>

        <div className="auro-header__right">
          <div className="auro-lang" role="group" aria-label={t('nav.language')}>
            <button
              type="button"
              className={currentLang === 'it' ? 'is-active' : ''}
              onClick={() => switchLang('it')}
              data-testid="lang-it"
            >IT</button>
            <span className="auro-lang__sep" aria-hidden="true">/</span>
            <button
              type="button"
              className={currentLang === 'en' ? 'is-active' : ''}
              onClick={() => switchLang('en')}
              data-testid="lang-en"
            >EN</button>
          </div>

          <button
            type="button"
            className={`auro-burger ${open ? 'is-open' : ''}`}
            aria-label={open ? t('nav.close') : t('nav.menu')}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="burger"
          >
            <span /><span /><span />
          </button>
        </div>
      </div>

      <div ref={menuRef} className={`auro-mobile ${open ? 'is-open' : ''}`} role="dialog" aria-modal={open ? 'true' : 'false'}>
        <a href="#how" onClick={goAnchor('how')}>{t('nav.how')}</a>
        <a href="#benefits" onClick={goAnchor('benefits')}>{t('nav.benefits')}</a>
        <a href="#transparency" onClick={goAnchor('transparency')}>{t('nav.transparency')}</a>
        <a href="#faq" onClick={goAnchor('faq')}>{t('nav.faq')}</a>
        <a href="#contact" onClick={goAnchor('contact')}>{t('nav.contact')}</a>
        <div className="auro-mobile__legal">
          <NavLink to={`/${currentLang}/privacy`}>{t('nav.privacy')}</NavLink>
          <NavLink to={`/${currentLang}/cookies`}>{t('nav.cookies')}</NavLink>
          <NavLink to={`/${currentLang}/terms`}>{t('nav.terms')}</NavLink>
        </div>
      </div>
    </header>
  );
}
